import { useEffect, useState, useRef, useCallback, MutableRefObject } from 'react';

import { Popover, Typography, Button, Box } from '@mui/material';

import { useStepOnboarding } from './hooks';

type Props = {
  storageKey: string;
  steps: string[];
  texts: string[];
  onRestartRef?: MutableRefObject<(() => void) | null>;
};

const ARROW_SIZE = 8;

export const StepByStepHints = ({ steps, texts, storageKey, onRestartRef }: Props) => {
  const { stepIndex, currentSelector, next, restart } = useStepOnboarding(storageKey, steps);

  useEffect(() => {
    if (onRestartRef) {
      onRestartRef.current = restart;
    }

    return () => {
      if (onRestartRef) {
        onRestartRef.current = null;
      }
    };
  }, [onRestartRef, restart]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const updateAnchor = useCallback(() => {
    if (currentSelector) {
      const el = document.querySelector(currentSelector);
      setAnchorEl(el as HTMLElement | null);
    } else {
      setAnchorEl(null);
    }
  }, [currentSelector]);

  useEffect(() => {
    updateAnchor();

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new MutationObserver(() => {
      updateAnchor();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [currentSelector, updateAnchor]);

  if (stepIndex === null) return null;
  if (anchorEl === null) return null;

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={next}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: '#e8f5e9',
            borderRadius: 2,
            border: '1px solid #5ecba1',
            boxShadow: '0 4px 20px rgba(94, 203, 161, 0.25)',
            overflow: 'visible',
            mt: `${ARROW_SIZE}px`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -ARROW_SIZE,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: `${ARROW_SIZE}px solid transparent`,
              borderRight: `${ARROW_SIZE}px solid transparent`,
              borderBottom: `${ARROW_SIZE}px solid #5ecba1`,
            },
          },
        },
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1, maxWidth: 280 }}>
        <Typography variant="caption" sx={{ color: '#2e7d55', fontWeight: 600 }}>
          Шаг {stepIndex + 1} из {steps.length}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: '#1b5e20' }}>
          {texts[stepIndex]}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            onClick={next}
            size="small"
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#5ecba1',
              '&:hover': { backgroundColor: '#4db890' },
              minWidth: 60,
            }}
          >
            {stepIndex + 1 < steps.length ? 'Далее' : 'Готово'}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
